import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  BookOpen, Brain, FileText, PenTool, Target, Monitor, Award,
  Lock, CheckCircle, PlayCircle, ChevronRight, Home, Loader2,
  Mail, LogOut, User, Eye, EyeOff, UserPlus, Heart, Microscope, ClipboardList
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_academia-sabeneval/artifacts/ai0i1vi4_2F29CCC3-E812-45AF-8948-674464C4314C.PNG";

const MODULE_ICONS = { BookOpen, Brain, FileText, PenTool, Target, Monitor, Award, Heart, Microscope, ClipboardList };
const MODULE_COLORS = {
  purple: "bg-purple-100 text-purple-600",
  blue: "bg-blue-100 text-blue-600",
  green: "bg-green-100 text-green-600",
  orange: "bg-orange-100 text-orange-600",
  red: "bg-red-100 text-red-600",
  teal: "bg-teal-100 text-teal-600",
  gold: "bg-yellow-100 text-yellow-600",
  pink: "bg-pink-100 text-pink-600",
  cyan: "bg-cyan-100 text-cyan-600"
};

export default function MiCurso() {
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(false);
  const [studentName, setStudentName] = useState("");
  const [studentId, setStudentId] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // For first-time password setup
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false);
  const [fullName, setFullName] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [settingUp, setSettingUp] = useState(false);

  useEffect(() => {
    const savedEmail = sessionStorage.getItem("studentEmail");
    const savedName = sessionStorage.getItem("studentName");
    const savedId = sessionStorage.getItem("studentId");
    if (savedEmail && savedName) {
      setHasAccess(true);
      setStudentName(savedName);
      setEmail(savedEmail);
      setStudentId(savedId || "");
      fetchModules();
    }
  }, []);

  const fetchModules = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/modules`);
      setModules(response.data.modules);
    } catch (error) {
      console.error("Error fetching modules:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Ingresa tu correo y contraseña");
      return;
    }

    setVerifying(true);
    try {
      const response = await axios.post(`${API}/student/login`, {
        email: email.toLowerCase(),
        password
      });
      
      if (response.data.success) {
        if (response.data.needs_password_setup) {
          // Legacy student needs to set up password
          setNeedsPasswordSetup(true);
          setFullName(response.data.student.full_name || "");
          toast.info("Por favor configura tu contraseña para continuar");
        } else {
          // Login successful
          const student = response.data.student;
          setHasAccess(true);
          setStudentName(student.full_name);
          setStudentId(student.id);
          sessionStorage.setItem("studentEmail", student.email);
          sessionStorage.setItem("studentName", student.full_name);
          sessionStorage.setItem("studentId", student.id);
          toast.success("¡Bienvenido!");
          fetchModules();
        }
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Error al iniciar sesión";
      toast.error(msg);
    } finally {
      setVerifying(false);
    }
  };

  const handleSetupPassword = async (e) => {
    e.preventDefault();
    if (!password || !confirmPassword) {
      toast.error("Completa todos los campos");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    if (password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    setSettingUp(true);
    try {
      const response = await axios.post(`${API}/student/setup-password`, {
        email: email.toLowerCase(),
        password,
        full_name: fullName
      });
      
      if (response.data.success) {
        const student = response.data.student;
        setHasAccess(true);
        setStudentName(student.full_name);
        setStudentId(student.id);
        setNeedsPasswordSetup(false);
        sessionStorage.setItem("studentEmail", student.email);
        sessionStorage.setItem("studentName", student.full_name);
        sessionStorage.setItem("studentId", student.id);
        toast.success("¡Contraseña configurada! Bienvenido al curso");
        fetchModules();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al configurar contraseña");
    } finally {
      setSettingUp(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("studentEmail");
    sessionStorage.removeItem("studentName");
    sessionStorage.removeItem("studentId");
    setHasAccess(false);
    setStudentName("");
    setEmail("");
    setPassword("");
    setNeedsPasswordSetup(false);
  };

  const getModuleProgress = (moduleId) => {
    const saved = localStorage.getItem(`module_${moduleId}_progress`);
    return saved ? parseInt(saved) : 0;
  };

  // Password Setup Screen
  if (needsPasswordSetup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl" data-testid="password-setup-card">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <UserPlus className="w-8 h-8 text-purple-600" />
            </div>
            <CardTitle className="font-heading text-2xl">Configura tu Contraseña</CardTitle>
            <p className="text-slate-500 text-sm mt-2">Es la primera vez que accedes. Configura una contraseña segura.</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSetupPassword} className="space-y-4">
              <div className="space-y-2">
                <Label>Correo electrónico</Label>
                <Input value={email} disabled className="bg-slate-50" />
              </div>
              <div className="space-y-2">
                <Label>Nombre completo</Label>
                <Input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Tu nombre"
                />
              </div>
              <div className="space-y-2">
                <Label>Nueva contraseña</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className="pr-10"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3 text-slate-400">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Confirmar contraseña</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite tu contraseña"
                />
              </div>
              <Button type="submit" disabled={settingUp} className="w-full bg-purple-600 hover:bg-purple-700 h-11">
                {settingUp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                {settingUp ? "Configurando..." : "Configurar y Acceder"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Login Screen
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl" data-testid="student-login-card">
          <CardHeader className="text-center pb-2">
            <img src={LOGO_URL} alt="Sabeneval" className="h-12 w-auto mx-auto mb-4" />
            <CardTitle className="font-heading text-2xl">Acceso al Curso</CardTitle>
            <p className="text-slate-500 text-sm mt-2">Ingresa con tu correo y contraseña</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  Correo electrónico
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  className="h-11"
                  data-testid="student-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-slate-400" />
                  Contraseña
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Tu contraseña"
                    className="h-11 pr-10"
                    data-testid="student-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={verifying} className="w-full bg-purple-600 hover:bg-purple-700 h-11" data-testid="student-login-btn">
                {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                {verifying ? "Verificando..." : "Ingresar al Curso"}
              </Button>
            </form>
            <div className="mt-6 text-center text-sm text-slate-500">
              <p>¿No tienes cuenta?</p>
              <p className="mt-1">Primero <a href="/inscripcion" className="text-purple-600 hover:underline font-medium">inscríbete aquí</a></p>
            </div>
            <div className="mt-4 text-center">
              <Button variant="ghost" onClick={() => navigate("/")} className="text-slate-500">
                <Home className="w-4 h-4 mr-2" />
                Volver al inicio
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Course Dashboard
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 py-4 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Sabeneval" className="h-10 w-auto" />
              <span className="font-heading font-bold text-lg text-purple-700 hidden sm:block">SABENEVAL</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600 hidden sm:inline">Hola, <strong>{studentName}</strong></span>
            </div>
            <Button onClick={handleLogout} variant="outline" size="sm" className="text-red-600 border-red-200">
              <LogOut className="w-4 h-4 mr-2" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Sección de Bienvenida */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 mb-8" data-testid="welcome-section">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-purple-800 text-center mb-6">
            Bienvenidos a Sabeneval
          </h2>
          <div className="max-w-3xl mx-auto text-center space-y-4 text-slate-600">
            <p>
              En Sabeneval creemos que la educación de calidad abre puertas a nuevas oportunidades. 
              Con casi <strong>8 años de experiencia</strong> en el mercado y más de <strong>30 años de docencia</strong>, 
              somos un equipo multidisciplinario comprometido con el aprendizaje de nuestros alumnos.
            </p>
            <p>
              Nuestra prioridad es tu formación profesional. Trabajamos con ética, responsabilidad y esfuerzo 
              total para que logres tus objetivos y estés preparado para los retos del área de la salud.
            </p>
            <p>
              Cada día buscamos mejorarnos y crecer, porque sabemos que la constancia y la dedicación 
              son la clave del éxito. <strong className="text-purple-700">En Sabeneval, tu aprendizaje es nuestra pasión.</strong>
            </p>
          </div>
        </div>

        {/* Mi Curso Banner */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl p-6 text-white mb-8">
          <h1 className="font-heading text-2xl font-bold">Mi Curso EXANI II</h1>
          <p className="text-purple-100 mt-2">Continúa tu preparación para el examen de admisión</p>
          <div className="flex items-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              <span>9 Módulos</span>
            </div>
            <div className="flex items-center gap-2">
              <Monitor className="w-5 h-5" />
              <span>3 Simuladores</span>
            </div>
          </div>
        </div>

        {/* Modules Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module) => {
              const Icon = MODULE_ICONS[module.icon] || BookOpen;
              const colorClass = MODULE_COLORS[module.color] || MODULE_COLORS.purple;
              const progress = getModuleProgress(module.id);
              
              return (
                <Card
                  key={`module-${module.id}`}
                  className="hover:shadow-lg transition-all cursor-pointer group"
                  onClick={() => navigate(`/mi-curso/modulo/${module.id}`)}
                  data-testid={`module-card-${module.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`w-12 h-12 ${colorClass} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-slate-400">Módulo {module.id}</span>
                          {progress === 100 && <CheckCircle className="w-4 h-4 text-green-500" />}
                        </div>
                        <h3 className="font-semibold text-slate-900 group-hover:text-purple-600 transition-colors">
                          {module.title}
                        </h3>
                      </div>
                    </div>
                    <p className="text-sm text-slate-500 mb-4 line-clamp-2">{module.description}</p>
                    
                    {/* Progress Bar */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-400">Progreso</span>
                        <span className="font-medium text-slate-600">{progress}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>
                    
                    <div className="flex items-center justify-end mt-4">
                      <Button variant="ghost" size="sm" className="text-purple-600 group-hover:translate-x-1 transition-transform">
                        {progress > 0 ? "Continuar" : "Comenzar"}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
